import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // Import useNavigate
import { Bars3Icon, XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

//Images
import Logo from "../images/logo.png";

//Api Reference
import apiSpec from '../benepay-b2b-api.json';

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const [activeItem, setActiveItem] = useState();
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation(); // Get current route location
  const [navigationItems, setNavigationItems] = useState([
    { type: "heading", name: "Introduction" },
    { type: "link", name: "Get started", href: "/getStart" },
    { type: "link", name: "API Security", href: "/encryption" },
    { type: "heading", name: "API Reference" },
    { type: "link", name: "Authentication", href: "/authentication", reqType: "post" },
  ]);

  const handleNavigation = (href) => {
    setActiveItem(href);
    navigate(href); // Redirect to the selected page
  };

  useEffect(() => {
    setActiveItem(location.pathname); // Update active item when location changes
  }, [location]);

  // Log API paths and their summaries
  useEffect(() => {
    if (!apiSpec?.paths) return;

    const dynamicItems = Object.entries(apiSpec.paths).map(([path, values]) => {
      const reqType = values.post ? "post" : values.get ? "get" : null;
      const reqData = values.post ? values.post : values.get;
      const summary = values.post?.summary || values.get?.summary || path;

      if( reqData && reqData.showInMenu ){
        return {
          type: "link",
          name: summary,
          href: `/${summary.replace(/\s+/g, "")}`,
          reqType,
        };
      } else {
        return {};
      }
    });

    setNavigationItems((prevItems) => {
      // Check if API Reference heading exists
      const hasApiRefHeading = prevItems.some(item => item.type === "heading" && item.name === "API Reference");
      
      const hasCustomPageHeading = dynamicItems.some(item => item.type === "link" && item.name === "Custom Payment");
      
      if( !hasCustomPageHeading ){
        dynamicItems.push(
          { type: "link", name: "Custom Payment", href: "/customPayment", reqType: "get" },
          { type: "link", name: "Refund Request", href: "/refundRequest", reqType: "post" },
        );
      }

      let apimenus = [
        ...prevItems,
        ...(!hasApiRefHeading ? [{ type: "heading", name: "API Reference" }] : []), // Add heading if missing
        ...dynamicItems.filter(newItem => !prevItems.some(existingItem => existingItem.href === newItem.href) // Avoid duplicate API links
        ),
      ];

      // const hasSwaggerHeading = prevItems.some(item => item.type === "heading" && item.name === "API Tools");

      // if (!hasSwaggerHeading) {
      //   apimenus.push({ type: "heading", name: "API Tools" });
      //   apimenus.push({ type: "link", name: "Swagger", href: "/swagger"});
      // }
        
      const hasPluginHeading = prevItems.some(item => item.type === "heading" && item.name === "Plugin Integration");

      if( !hasPluginHeading ){
        apimenus.push({ type: "heading", name: "Plugin Integration" });
        apimenus.push({ type: "link", name: "WooCommerce", href: "/woocommerce"});
      }

      return apimenus;
    });
  }, [apiSpec]);

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity ${sidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"} md:hidden`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-[#f8f9fa] text-white transform transition-transform
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:fixed md:top-0 md:left-0 md:h-full md:w-64 md:flex md:flex-col`}
      >
        {/* Logo Section */}
        <div className="hidden md:flex items-center justify-center p-2 pl-2 pr-4 pb-0 pt-0 w-full">
          <a href="https://benepay.io/">
            <img src={Logo} alt="Brand Logo" className="h-auto w-48" />
          </a>

          {/* <select className="p-1 border rounded text-black focus:border-gray-300 focus:outline-none">
            <option value="v1">v1</option>
            <option value="v2">v2</option>
          </select> */}
        </div>

        {/* Search Bar */}
        <div className="hidden md:flex items-center justify-start pl-3 pr-4 pb-0">
          <div className="relative w-full">
            <hr className="border-t border-gray-300 w-full" />
            {/* <input
              id="search"
              name="search"
              type="text"
              placeholder="Search"
              className="block w-full rounded-md border border-gray-300 py-1.5 pr-10 pl-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400 sm:text-sm"
            />
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" /> */}
          </div>
        </div>

        {/* Mobile Close Button */}
        <div className="p-4 flex justify-between items-center md:hidden">
          <a href="https://benepay.io/">
            <img src={Logo} alt="Brand Logo" className="h-auto w-24" />
          </a>
          <button onClick={() => setSidebarOpen(false)}>
            <XMarkIcon className="w-6 h-6 text-black" />
          </button>
        </div>

        {/* Navigation Menu */}
        <div className="space-y-2 p-4">
          {navigationItems.map((item, index) =>
            item.type === "heading" ? (
              // Render Heading (Non-clickable)
              <div key={index} className="text-gray-500 text-sm font-semibold mt-1 mb-1 uppercase">
                {item.name}
              </div>
            ) : (
              // Render Navigation Link (Clickable)
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href)}
                className={classNames(
                  activeItem === item.href ? "bg-blue-100 text-gray-700 font-bold" : "hover:bg-gray-200 text-black",
                  "block w-full text-left px-4 py-2 rounded-md text-sm transition"
                )}
              >
                <div className="grid grid-cols-[1fr_auto] items-center w-full gap-2">
                  <p className="">{item.name}</p>
                  {item.reqType && (
                    <span
                      className={`px-2 text-[11px] font-semibold rounded-md text-center min-w-[40px]
                      ${item.reqType === "post" ? "bg-[#0171c2] text-white" :
                          item.reqType === "get" ? "bg-yellow-600 text-white" :
                            "bg-gray-400 text-white"
                        }`}
                    >
                      {item.reqType.toUpperCase()}
                    </span>
                  )}
                </div>


              </button>
            )
          )}
        </div>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 w-full">
        {/* Mobile Navbar */}
        <div className="md:hidden flex justify-between items-center p-4 bg-gray-800 text-white fixed top-0 left-0 w-full">
          <span className="text-lg font-bold">
            <a href="/">
              <img
                src={Logo} // Change to your logo path
                alt="Brand Logo"
                className="h-auto w-24 bg-white"
              />
            </a>
          </span>

          <div className="relative w-full px-6">
            {/* <input
              id="price"
              name="price"
              type="text"
              placeholder="Search"
              className="block w-full h-[33px] rounded-md border border-gray-300 py-1.5 pr-10 pl-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400 sm:text-sm"
            />
            <MagnifyingGlassIcon className="absolute right-8 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" /> */}
          </div>

          <button onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="w-6 h-6 " />
          </button>
        </div>

        {/* Page Content */}
        <div className="p-6 mt-9 md:pl-[280px] md:mt-0">{children}</div>
      </div>
    </div>
  );
}
